import { Literal, Static, Union } from "runtypes";
import { z } from "zod";

import { AgentChatRunId } from "../../idTypeBrands.js";
import { getNormalEnum } from "../../runtypeEnums.js";

import { AgentChatMessageSource } from "./agentChatMessageTypes.js";
import { AGENT_CHAT_MESSAGE_SOURCE_DISPLAY_NAME } from "./source";

export const AgentChatThreadTypeLiteral = Union(
  // MagicEvent-based threads
  // AgentChatMessage-based threads
  Literal("LOGIC_VIEW_V2"),
  Literal("ASK"),
  Literal("REVIEW"),
  Literal("ARTIFACT_EXTRACTION"),
  Literal("SEMANTIC_AUTHORING"),
  // Unused/dormant
  Literal("LOGIC_VIEW"),
  Literal("CHAT_WITH_APP"),
  Literal("EXPLORE"),
);
export type AgentChatThreadType = Static<typeof AgentChatThreadTypeLiteral>;
export const AgentChatThreadType = getNormalEnum(AgentChatThreadTypeLiteral);

// TODO: add AgentChatThreadType.ANALYTICS when that gets added
export const ANALYTICS_AGENT_TYPES: AgentChatThreadType[] = [
  AgentChatThreadType.LOGIC_VIEW_V2,
  AgentChatThreadType.ASK,
];

export const AgentChatThreadAutoSaveHexVersionStatusLiteral = Union(
  Literal("PENDING"),
  Literal("SUCCEEDED"),
  Literal("FAILED"),
);
export type AgentChatThreadAutoSaveHexVersionStatus = Static<
  typeof AgentChatThreadAutoSaveHexVersionStatusLiteral
>;
export const AgentChatThreadAutoSaveHexVersionStatus = getNormalEnum(
  AgentChatThreadAutoSaveHexVersionStatusLiteral,
);

/**
 * Reason why a thread is returned as an `UnknownAgentChatThread`:
 *  - `SENSITIVE`: the thread is private, and contents are hidden from everyone except the user who created it.
 *  - `ACCESS_DOWNGRADED`: the user does not have permission to view this thread
 *    (e.g. they lack access to a data connection used within the thread, secret, etc.).
 */
export const AgentChatThreadRestrictedReasonLiteral = Union(
  Literal("SENSITIVE"),
  Literal("ACCESS_DOWNGRADED"),
);
export type AgentChatThreadRestrictedReason = Static<
  typeof AgentChatThreadRestrictedReasonLiteral
>;
export const AgentChatThreadRestrictedReason = getNormalEnum(
  AgentChatThreadRestrictedReasonLiteral,
);

/**
 * User-facing display names for each `AgentChatThreadType`.
 */
export const AGENT_CHAT_THREAD_TYPE_DISPLAY_NAME: Record<
  AgentChatThreadType,
  string
> = {
  [AgentChatThreadType.ASK]: "Threads",
  [AgentChatThreadType.LOGIC_VIEW]: "Notebook",
  [AgentChatThreadType.LOGIC_VIEW_V2]: "Notebook",
  [AgentChatThreadType.REVIEW]: "Review",
  [AgentChatThreadType.ARTIFACT_EXTRACTION]: "Artifact Extraction",
  [AgentChatThreadType.SEMANTIC_AUTHORING]: "Modeling",
  [AgentChatThreadType.EXPLORE]: "Explore",
  [AgentChatThreadType.CHAT_WITH_APP]: "Published app",
};

/**
 * Types that only appear as subagent identifiers, never as AgentChatThread.type in the DB.
 */
export const AgentSubagentTypeLiteral = Union(
  Literal("DATA_DISCOVERY"),
  Literal("VISUALIZATION"),
  Literal("DATA_APP"),
);
export type AgentSubagentType = Static<typeof AgentSubagentTypeLiteral>;
export const AgentSubagentType = getNormalEnum(AgentSubagentTypeLiteral);

/**
 * Combined type covering both user-facing thread types and subagent-only types.
 */
export type AgentChatThreadOrSubagentType =
  | AgentChatThreadType
  | AgentSubagentType;

export type LogicViewChatState = "closed" | "chat" | "history";
export const AgentEntryPointLiteral = Union(
  Literal("FIX_CELL_WITH_AGENT"),
  Literal("SLACK"),
  Literal("MCP"),
  Literal("AGENT_SUGGESTION"),
  Literal("CONTEXT_WORKBENCH_PREVIEW"),
  Literal("SEMANTIC_PROJECT_WORKBENCH_PREVIEW"),
  Literal("PUBLISHED_APP"),
  Literal("PUBLIC_API"),
  Literal("TASK"),
  Literal("CUSTOMER_EVAL"),
);
export type AgentEntryPoint = Static<typeof AgentEntryPointLiteral>;
export const AgentEntryPoint = getNormalEnum(AgentEntryPointLiteral);

export type AppAgentChatState = "closed" | "chat" | "history";

/**
 * Context for subagent message isolation.
 * Contains both the subagent type and a unique invocation ID.
 */
export interface SubagentContext {
  subagentType: AgentSubagentType;
  subagentInvocationId: AgentChatRunId;
}

/**
 * Status for queued prompts in an agent chat thread.
 */
export const QueuedPromptStatusLiteral = Union(
  /** Prompt is waiting to be processed */
  Literal("PENDING"),
  /** Prompt is marked to run next, ahead of the normal queue */
  Literal("FORCED"),
);
export type QueuedPromptStatus = Static<typeof QueuedPromptStatusLiteral>;
export const QueuedPromptStatus = getNormalEnum(QueuedPromptStatusLiteral);

/**
 * Upper bound on the number of attachments (mentioned tables, datasets, views,
 * projects, cells, and data connections) that can be sent when creating a
 * thread. Defends against unnecessary prompt bloat since attachments are
 * inlined into the prompt.
 */
export const MAX_PROMPT_ATTACHMENTS = 150;

/**
 * Source of a thread as exposed via the public/external APIs.
 *
 * TODO(@acorn): consider merging this with {@link AgentChatMessageSource}. As
 * of this writing, it's unclear if these should be 1:1 and we want to be
 * intentional about what we expose via the API.
 */
export const ApiThreadsSourceSchema = z.enum([
  "HEX",
  "SLACK",
  "MCP",
  "PUBLIC_API",
]);
export type ApiThreadsSource = z.infer<typeof ApiThreadsSourceSchema>;

export const getAgentChatThreadTypeDisplayName = (
  type: AgentChatThreadType,
): string => {
  return AGENT_CHAT_THREAD_TYPE_DISPLAY_NAME[type] ?? "Unknown";
};

export const getAgentChatMessageSourceDisplayName = (
  source: AgentChatMessageSource | null | undefined,
): string => {
  if (source == null) {
    return AGENT_CHAT_MESSAGE_SOURCE_DISPLAY_NAME[AgentChatMessageSource.HEX];
  }
  return AGENT_CHAT_MESSAGE_SOURCE_DISPLAY_NAME[source];
};
